import { Suspense } from 'react';
import { Clock, Filter } from 'lucide-react';
import { usePlugins } from '@reflex/ui/client/hooks';
import type { FlowTriggerNodeConfig } from '@reflex/flows';
import { rendererKeyForTrigger } from '../../shared/event-catalog';
import { useTriggerExternalRefs } from '../useExternalRefs';
import { FlowNodeBase } from './FlowNodeBase';

interface FlowTriggerNodeData {
  label: string;
  config: FlowTriggerNodeConfig;
  status?: string;
  /** Run payload, present on run surfaces for the fired trigger node. */
  triggerData?: Record<string, unknown> | null;
}

function kindSubtype(config: FlowTriggerNodeConfig): string {
  switch (config.kind) {
    case 'event':
      return 'Event';
    case 'cron':
      return 'Schedule';
    case 'manual':
      return 'Manual';
    case 'service':
      return 'Service';
  }
}

function eventDescription(config: FlowTriggerNodeConfig): string | undefined {
  if (config.kind !== 'event') return undefined;
  return config.action
    ? `${config.source} · ${config.event}.${config.action}`
    : `${config.source} · ${config.event}`;
}

/** Compact summary of an event trigger's embedded filter, shown on the card. */
function EmbeddedFilterSummary({ config }: { config: FlowTriggerNodeConfig & { kind: 'event' } }) {
  const filter = config.filter;
  if (!filter) return null;

  let summary: string | null = null;
  if (filter.mode === 'expression') {
    if (filter.expression.length > 0) summary = 'expression';
  } else {
    const conditionCount = filter.conditions?.length ?? 0;
    if (conditionCount > 0) {
      summary = `${conditionCount} condition${conditionCount === 1 ? '' : 's'} (${
        filter.match === 'any' ? 'any' : 'all'
      })`;
    }
  }
  if (!summary) return null;

  return (
    <div
      className="flex items-center gap-1.5 rounded bg-muted px-2 py-1"
      data-testid="trigger-filter-summary"
    >
      <Filter className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="text-[10px] text-muted-foreground">Filter: {summary}</span>
    </div>
  );
}

export function FlowTriggerNode({
  data,
  selected,
}: {
  data: FlowTriggerNodeData;
  selected?: boolean;
}) {
  const { flowTriggerRenderers } = usePlugins();
  const config = data.config;
  const externalRefs = useTriggerExternalRefs(
    config.kind === 'event' ? { source: config.source, event: config.event } : undefined,
    data.triggerData ?? null,
  );
  const renderer = rendererKeyForTrigger(config)
    .map((key) => flowTriggerRenderers.get(key))
    .find((r) => r !== undefined);
  const RendererComponent = renderer?.component;
  const hasTriggerData = data.triggerData && Object.keys(data.triggerData).length > 0;
  const fields = config.kind === 'manual' ? (config.fields ?? []) : [];

  return (
    <FlowNodeBase
      nodeType="trigger"
      label={data.label}
      tagSubtype={kindSubtype(config)}
      selected={selected}
      isActive={data.status === 'running'}
      status={data.status}
      hideTargetHandle
      testId="flow-trigger-node"
      description={eventDescription(config)}
    >
      {RendererComponent && hasTriggerData && (
        <Suspense fallback={null}>
          <RendererComponent
            triggerData={data.triggerData ?? null}
            surface="graph"
            externalRefs={externalRefs}
          />
        </Suspense>
      )}

      {config.kind === 'cron' && (
        <div className="flex items-center gap-1.5 rounded bg-muted/50 px-2 py-1">
          <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
          <code className="text-[10px] text-foreground/80">{config.schedule}</code>
          {config.timezone && (
            <span className="text-[9px] text-muted-foreground">({config.timezone})</span>
          )}
        </div>
      )}

      {config.kind === 'event' && <EmbeddedFilterSummary config={config} />}

      {config.kind === 'manual' && config.model && (
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {config.model}
          </span>
          {fields.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{fields.length} fields</span>
          )}
        </div>
      )}

      {config.kind === 'manual' && !config.model && fields.length > 0 && fields.length <= 4 && (
        <div className="flex flex-col gap-0.5">
          {fields.map((f) => (
            <div key={f.name} className="flex items-center gap-1.5 text-[10px]">
              <span className="rounded bg-muted px-1 py-px font-mono text-muted-foreground">
                {f.type}
              </span>
              <span className="text-foreground/80">
                {f.name}
                {f.required && <span className="ml-0.5 text-status-warning">*</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {config.kind === 'manual' && !config.model && fields.length > 4 && (
        <span className="text-[10px] text-muted-foreground">
          {fields.length} input fields ({fields.filter((f) => f.required).length} required)
        </span>
      )}
    </FlowNodeBase>
  );
}
